import { BusUnlockFailureReason } from "components/detailPane/BusDetailPane/busUnlockState";
import { ExportType, KMZ_TYPE } from "components/sidebar/exports/exportHelpers";
import {
    BusFilters,
    CapacityThresholds,
    MaybeMaxCosts
} from "types/busFilterTypes";
import {
    Bus,
    BusId,
    CapacityEnergyCost,
    NiraBusId,
    ScopedBus,
    UnauthorizedBus
} from "types/busType";
import { GeneratorType } from "types/generatorType";
import { LayerStyleId } from "types/layerStyleTypes";
import { RegionType } from "types/regionType";
import { ResourceType } from "types/resourceType";
import { ScopeName } from "types/scope";
import { ScopeViewType } from "types/scopeViewType";
import { ScreeningViewId } from "types/screeningViewType";
import { FacilityId } from "types/selectedFacility";
import { UrlDirectoryType } from "types/urlDirectoryType";
import { TeamId } from "types/userType";
import { Voltage } from "types/voltageTypes";
import { track } from "./analytics";

const VOLTAGE_DROPDOWN_CHANGED_NAME = "voltage_dropdown_changed";
const NEW_VOLTAGE_PROPERTY = "new_voltage";
const OLD_VOLTAGE_PROPERTY = "old_voltage";
const VOLTAGE_CHANGE_SOURCE_PROPERTY = "source";
export type VoltageFilterChangeSource = "voltage_dropdown" | "search_result";

const MAX_ALLOCATED_COSTS_DROPDOWN_CHANGED_NAME =
    "max_allocated_costs_dropdown_changed";
const NEW_MAX_ALLOCATED_COSTS_PROPERTY = "new_max_allocated_costs";
const OLD_MAX_ALLOCATED_COSTS_PROPERTY = "old_max_allocated_costs";
const NEW_MAX_ALLOCATED_COSTS_ENABLED_PROPERTY =
    "new_max_allocated_costs_enabled";
const OLD_MAX_ALLOCATED_COSTS_ENABLED_PROPERTY =
    "old_max_allocated_costs_enabled";

const TOTAL_COSTS_DROPDOWN_CHANGED_NAME = "total_costs_dropdown_changed";
const NEW_TOTAL_COSTS_PROPERTY = "new_max_total_cost";
const OLD_TOTAL_COSTS_PROPERTY = "old_max_total_cost";
const NEW_TOTAL_COSTS_ENABLED_PROPERTY = "new_total_costs_enabled";
const OLD_TOTAL_COSTS_ENABLED_PROPERTY = "old_total_costs_enabled";

const BUS_SELECTED_FROM_SIDEBAR_NAME = "bus_selected_from_sidebar";
const BUS_SELECTED_FROM_SIDEBAR_SOURCE_PROPERTY = "source";
// The favorites panel renders the same row component as the results list.
export type BusSelectedFromSidebarSource = "results_list" | "favorites_panel";
const BUS_SELECTED_FROM_LINE_DETAIL_NAME = "bus_selected_from_line_detail";
const UNAUTHORIZED_BUS_SELECTED_FROM_SIDEBAR_NAME =
    "unauthorized_bus_selected_from_sidebar";
const UNLOCK_BUS_ATTEMPT = "unlock_bus_attempt";
const UNAUTHORIZED_BUS_DETAIL_PANE_SHOWN_NAME =
    "unauthorized_bus_detail_pane_shown";
const UNLOCK_FAILURE_REASON_PROPERTY = "unlock_failure_reason";
const UNLOCK_BUS_CONFIRMATION_SHOWN_NAME = "unlock_bus_confirmation_shown";
const UNLOCK_BUS_CONFIRMATION_CONFIRMED_NAME =
    "unlock_bus_confirmation_confirmed";
const SELECTION_CANCELED_FROM_MAP_NAME = "selection_canceled_from_map";
const SELECTION_CANCELED_FROM_BUS_DETAIL_CROSS =
    "selection_canceled_from_bus_detail_cross";
const SELECTION_CANCELED_FROM_LINE_DETAIL_CROSS =
    "selection_canceled_from_line_detail_cross";
const BUS_SELECTED_FROM_MAP_NAME = "bus_selected_from_map";
const UNAUTHORIZED_BUS_SELECTED_FROM_MAP_NAME =
    "unauthorized_bus_selected_from_map";
const BUS_SELECTED_FROM_TOGGLE_NAME = "bus_selected_from_toggle";
const CONNECTED_LINES_SECTION_TOGGLED_NAME = "connected_lines_section_toggled";
const LINE_SELECTED_FROM_BUS_DETAIL_PANE_NAME =
    "line_selected_from_bus_detail_pane";

const BUS_ID_PROPERTY = "bus_id";
// The identifier that actually names one bus. `bus_id` carries `bus.id`, which
// is not unique -- 16,957 of the 105,971 buses in the table share theirs with
// another bus, whether across regions or across a customer's own model scopes
// (`PJM-235101`, `PJM-SCOUT-235101`, ... are all one substation). `nira_bus_id`
// is unique for every one of them, so it is the identifier to key analytics on.
// Only the unlock events carry it so far; the rest of the bus events are a
// separate migration, and `bus_id` is left alone everywhere so no existing
// series changes meaning underneath a dashboard.
const NIRA_BUS_ID_PROPERTY = "nira_bus_id";
const FACILITY_ID_PROPERTY = "facility_id";
const SELECTED_ID_PROPERTY = "selected_id";
const BUS_VOLTAGE_PROPERTY = "bus_voltage";
const BUS_DISPLAY_NAME_PROPERTY = "bus_display_name";
const BUS_REGION_PROPERTY = "bus_region";
const BUS_OWNER_NAME_PROPERTY = "bus_owner_name";
// Renamed from the older `unlock_bus_successful`, which was sent inverted (true
// when the unlock had *failed*) from its introduction in Feb 2023 until this
// property replaced it. Correcting the value under the old name would have
// silently flipped the meaning of a three-year-old series mid-chart, so the old
// property is retired instead: history keeps its (documented) meaning, and
// everything from here on reads off this one.
const BUS_UNLOCK_SUCCEEDED_PROPERTY = "bus_unlock_succeeded";
const BUSES_REMAINING_PROPERTY = "buses_remaining";
const CONNECTED_LINES_SECTION_OPENED_PROPERTY =
    "connected_lines_section_opened";
const IS_BUS_HIDDEN_PROPERTY = "is_bus_hidden";
const GENERATOR_TYPE_PROPERTY = "generator_type";
const URL_DIRECTORY_PROPERTY = "url_directory";

const CAPACITY_AVAILABLE_PROPERTY = "capacity_available";
const ENERGY_AVAILABLE_PROPERTY = "energy_available";
const EDITED_CAPACITY_AVAILABLE_PROPERTY = "capacity_available_edited";
const EDITED_ENERGY_AVAILABLE_PROPERTY = "energy_available_edited";
const EDITED_CHARGING_AVAILABLE_PROPERTY = "charging_available_edited";

const TOTAL_COSTS_PROPERTY = "total_costs";
const ALLOCATED_COSTS_PROPERTY = "allocated_costs";

const MENU_ITEM_PROPERTY = "menu_item";

const REGION_PROPERTY = "region";
const SCOPE_PROPERTY = "scope";
const SCOPE_VIEW_PROPERTY = "scope_view";

const MORE_DETAILS_CONSTRAINTS_DIALOG_OPENED_NAME =
    "more_details_constraints_dialog_opened";
const MORE_DETAILS_CONSTRAINTS_DIALOG_CLOSED_NAME =
    "more_details_constraints_dialog_closed";

const CUSTOM_INJECTION_DIALOG_OPENED_NAME = "custom_injection_dialog_opened";
const CUSTOM_INJECTION_DIALOG_CLOSED_NAME = "custom_injection_dialog_closed";

const CUSTOM_INJECTION_UPDATED_NAME = "custom_injection_updated";
const PREVIOUS_CAPACITY_AVAILABLE_PROPERTY = "capacity_available_previous";
const PREVIOUS_ENERGY_AVAILABLE_PROPERTY = "energy_available_previous";
const PREVIOUS_CHARGING_AVAILABLE_PROPERTY = "charging_available_previous";

const STARTED_ZOOMING_NAME = "started_zooming";
const ENDED_ZOOMING_NAME = "ended_zooming";
const STARTED_DRAGGING_NAME = "started_dragging";
const ENDED_DRAGGING_NAME = "ended_dragging";

const ZOOM_PROPERTY = "zoom";
const LATITUDE_PROPERTY = "latitude";
const LONGITUDE_PROPERTY = "longitude";

const FLY_TO_LOCATION_FROM_SEARCH_NAME = "fly_to_location_from_search";

const SEARCH_RESULT_ID_PROPERTY = "search_result_id";
const SEARCH_RESULT_SUB_TEXT_PROPERTY = "search_result_sub_text";

const SCOPE_CHANGED_NAME = "scope_changed";
const NEW_SCOPE_TYPE_SELECTED_PROPERTY = "new_scope_type";

const INCLUDES_PRE_EXISTING_CHANGED_NAME = "includes_pre_existing_changed";
const INCLUDES_PRE_EXISTING_PROPERTY = "includes_pre_existing";

const HIDE_LOW_CONFIDENCE_BUSES_CHANGED_NAME =
    "hide_low_confidence_buses_changed";
const HIDE_LOW_CONFIDENCE_BUSES_PROPERTY = "hide_low_confidence_buses";

const HIDE_OUT_OF_REGION_BUSES_CHANGED_NAME =
    "hide_out_of_region_buses_changed";
const HIDE_OUT_OF_REGION_BUSES_PROPERTY = "hide_out_of_region_buses";

const HIDE_LOCKED_BUSES_CHANGED_NAME = "hide_locked_buses_changed";
const HIDE_LOCKED_BUSES_PROPERTY = "hide_locked_buses";

const FAVORITES_FILTER_CHANGED_NAME = "favorites_filter_changed";
const FAVORITES_FILTER_ON_PROPERTY = "favorites_filter_on";
const FAVORITES_COUNT_PROPERTY = "favorites_count";
const FAVORITES_PANEL_OPENED_NAME = "favorites_panel_opened";
const FAVORITES_PANEL_SOURCE_PROPERTY = "source";
export type FavoritesPanelSource = "sidebar" | "all_filters_dialog";
const FAVORITE_BUS_TOGGLED_NAME = "favorite_bus_toggled";
const FAVORITED_PROPERTY = "favorited";
const FAVORITE_BUS_TOGGLE_SOURCE_PROPERTY = "source";
export type FavoriteBusToggleSource = "detail_pane" | "favorites_panel";

const COLOR_THRESHOLDS_CHANGED_NAME = "color_thresholds_changed";
const COLOR_THRESHOLDS_LOW_PROPERTY = "low_threshold";
const COLOR_THRESHOLDS_MEDIUM_PROPERTY = "medium_threshold";
const COLOR_THRESHOLDS_HIGH_PROPERTY = "high_threshold";

const SCREENING_VIEW_ID_PROPERTY = "screening_view_id";

const SCREENING_VIEW_COPY_LINK_NAME = "screening_view_copy_link";
const SCREENING_VIEW_DELETE_NAME = "screening_view_delete";
const SCREENING_VIEW_CREATE_NAME = "screening_view_create";
const SCREENING_VIEW_EXPORT_KMZ_NAME = "screening_view_export_kmz";
const SCREENING_VIEW_EXPORT_SHAPEFILE_NAME = "screening_view_export_shapefile";

const DATA_FRESHNESS_DIALOG_CLOSED_NAME = "data_freshness_dialog_closed";
const DATA_FRESHNESS_DIALOG_DOWNLOAD_QUEUE_PROJECTS_NAME =
    "data_freshness_dialog_download_queue_projects";

const SIDEBAR_MENU_ITEM_CLICKED_NAME = "sidebar_menu_item_clicked";

const ADVANCED_STUDIES_NEW_ADVANCED_STUDY_MENU_ITEM_CLICKED_NAME =
    "advanced_studies_new_advanced_study_menu_item_clicked";
const ADVANCED_STUDIES_VIEW_ADVANCED_STUDIES_MENU_ITEM_CLICKED_NAME =
    "advanced_studies_view_advanced_studies_menu_item_clicked";
const ADVANCED_STUDIES_NEW_ADVANCED_STUDY_DIALOG_CLOSED_NAME =
    "advanced_studies_new_advanced_study_dialog_closed";

const ADVANCED_STUDY_ID_PROPERTY = "advanced_study_id";
const VIEW_ADVANCED_STUDIES_SOURCE_PROPERTY = "source";
export type ViewAdvancedStudiesSource = "bus_detail_pane" | "nav_actions";
const ADVANCED_STUDIES_NEW_ADVANCED_STUDY_DIALOG_CREATE_STUDY_CLICKED_NAME =
    "advanced_studies_new_advanced_study_dialog_create_study_clicked";

/**
 * View metadata (which region / fuel-type page the user is on) attached to
 * every prospecting event. Set by RoutingMetadataProvider when the user is
 * on a map view (e.g. /prospecting/miso-solar) and cleared on the home view
 * or when leaving the prospecting app, so these values don't have to be
 * threaded through every track call site.
 */
export type ProspectingViewTrackingContext = {
    readonly region: RegionType;
    readonly generatorType: GeneratorType;
    readonly urlDirectory: UrlDirectoryType;
};

let currentProspectingViewContext: ProspectingViewTrackingContext | undefined;

export const setProspectingViewTrackingContext = (
    context: ProspectingViewTrackingContext | undefined
): void => {
    currentProspectingViewContext = context;
};

/**
 * Clears the view context only if it is still the exact value this caller
 * set. During route transitions React renders the new tree before running
 * the old tree's unmount cleanup, so an unconditional clear on unmount could
 * wipe out a context that a newly-mounted provider just set.
 */
export const clearProspectingViewTrackingContext = (
    context: ProspectingViewTrackingContext
): void => {
    if (currentProspectingViewContext === context) {
        currentProspectingViewContext = undefined;
    }
};

/**
 * Tags an event with the current prospecting view. Explicit `properties` win
 * over the context, so a call site that already knows its own region -- the
 * bus page, which lives outside the map view -- can override it.
 *
 * TODO: deliberately deferred -- the context is read at emit time, so an
 * event emitted after async work carries whichever view is current when it
 * sends, not the one the user acted from. Three emitters are affected:
 * `segmentTrackUnlockBusAttempt` (unlock mutation callbacks),
 * `segmentTrackScreeningViewCreate` (mutation completion) and
 * `segmentTrackBusPageProjectSizeChanged` (debounce plus unmount flush).
 * Change pages inside that window and the event is tagged with the
 * destination view. Accepted for now: nothing user-facing, the mislabeled
 * share is small, and Segment's own auto-captured page context
 * (url/referrer) is read at send time too, so the module-level context is
 * not what introduces this. The fix is to capture the view when the action
 * starts and pass it in here, which changes this signature and every async
 * call site -- the exact threading this context exists to avoid. See
 * https://github.com/Nira-Energy/nira-app/pull/1604#discussion_r3873387887
 */
const trackProspectingEvent = (
    name: string,
    properties: Record<
        string,
        number | boolean | string | ReadonlyArray<number>
    >
): void => {
    if (currentProspectingViewContext === undefined) {
        track(name, properties);
        return;
    }

    track(name, {
        [REGION_PROPERTY]: currentProspectingViewContext.region,
        [GENERATOR_TYPE_PROPERTY]: currentProspectingViewContext.generatorType,
        [URL_DIRECTORY_PROPERTY]: currentProspectingViewContext.urlDirectory,
        ...properties
    });
};

export const segmentTrackVoltageDropdownChange = (
    newVoltages: readonly Voltage[],
    oldBusFilter: BusFilters,
    source: VoltageFilterChangeSource
): void => {
    trackProspectingEvent(VOLTAGE_DROPDOWN_CHANGED_NAME, {
        [NEW_VOLTAGE_PROPERTY]: newVoltages,
        [OLD_VOLTAGE_PROPERTY]: oldBusFilter.voltages,
        [VOLTAGE_CHANGE_SOURCE_PROPERTY]: source
    });
};

export const segmentTrackMaxAllocatedCostsDropdownChange = (
    newMaxAllocatedCosts: MaybeMaxCosts,
    oldMaxAllocatedCosts: MaybeMaxCosts
): void => {
    trackProspectingEvent(MAX_ALLOCATED_COSTS_DROPDOWN_CHANGED_NAME, {
        [NEW_MAX_ALLOCATED_COSTS_PROPERTY]: newMaxAllocatedCosts.maxCosts,
        [NEW_MAX_ALLOCATED_COSTS_ENABLED_PROPERTY]:
            newMaxAllocatedCosts.enabled,
        [OLD_MAX_ALLOCATED_COSTS_PROPERTY]: oldMaxAllocatedCosts.maxCosts,
        [OLD_MAX_ALLOCATED_COSTS_ENABLED_PROPERTY]: oldMaxAllocatedCosts.enabled
    });
};

export const segmentTrackMaxTotalCostsDropdownChange = (
    newTotalCosts: MaybeMaxCosts,
    oldTotalCosts: MaybeMaxCosts
): void => {
    trackProspectingEvent(TOTAL_COSTS_DROPDOWN_CHANGED_NAME, {
        [NEW_TOTAL_COSTS_PROPERTY]: newTotalCosts.maxCosts,
        [NEW_TOTAL_COSTS_ENABLED_PROPERTY]: newTotalCosts.enabled,
        [OLD_TOTAL_COSTS_PROPERTY]: oldTotalCosts.maxCosts,
        [OLD_TOTAL_COSTS_ENABLED_PROPERTY]: oldTotalCosts.enabled
    });
};
export const segmentTrackBusSelectedFromSidebar = (
    scopedBus: ScopedBus,
    source: BusSelectedFromSidebarSource
): void => {
    trackProspectingEvent(BUS_SELECTED_FROM_SIDEBAR_NAME, {
        ...convertScopedBusToSegmentProperties(scopedBus),
        [BUS_SELECTED_FROM_SIDEBAR_SOURCE_PROPERTY]: source
    });
};

export const segmentTrackUnauthorizedBusSelectedFromSidebar = (
    bus: UnauthorizedBus
): void => {
    trackProspectingEvent(UNAUTHORIZED_BUS_SELECTED_FROM_SIDEBAR_NAME, {
        [BUS_ID_PROPERTY]: bus.bus.id
    });
};

// `bus_id` here has carried the *nira* bus id since Feb 2023 -- the odd one out
// among the bus events, which send `bus.id`. It keeps doing so, unchanged, so
// three years of charts keep working; `nira_bus_id` is sent alongside it under
// the honest name, and is what the three events below join against.
export const segmentTrackUnlockBusAttempt = (
    niraBusId: NiraBusId,
    unlockSuccessful: boolean
): void => {
    trackProspectingEvent(UNLOCK_BUS_ATTEMPT, {
        [BUS_ID_PROPERTY]: niraBusId,
        [NIRA_BUS_ID_PROPERTY]: niraBusId,
        [BUS_UNLOCK_SUCCEEDED_PROPERTY]: unlockSuccessful
    });
};

// Fired when a bus selection lands on the locked pane instead of the real detail
// pane, tagged with which refusal reason the user was shown. Counts how often
// each wall gets hit -- e.g. how many readers are blocked purely by their role.
export const segmentTrackUnauthorizedBusDetailPaneShown = (
    niraBusId: NiraBusId,
    unlockFailureReason: BusUnlockFailureReason
): void => {
    trackProspectingEvent(UNAUTHORIZED_BUS_DETAIL_PANE_SHOWN_NAME, {
        [NIRA_BUS_ID_PROPERTY]: niraBusId,
        [UNLOCK_FAILURE_REASON_PROPERTY]: unlockFailureReason
    });
};

// The two events below track the unlock prompt a bus-limited team gets before an
// unlock spends one of their buses. Declining is "shown without confirmed":
// closing the pane already emits selection_canceled_from_bus_detail_cross.
export const segmentTrackUnlockBusConfirmationShown = (
    niraBusId: NiraBusId,
    busesRemaining: number
): void => {
    trackProspectingEvent(UNLOCK_BUS_CONFIRMATION_SHOWN_NAME, {
        [NIRA_BUS_ID_PROPERTY]: niraBusId,
        [BUSES_REMAINING_PROPERTY]: busesRemaining
    });
};

export const segmentTrackUnlockBusConfirmationConfirmed = (
    niraBusId: NiraBusId,
    busesRemaining: number
): void => {
    trackProspectingEvent(UNLOCK_BUS_CONFIRMATION_CONFIRMED_NAME, {
        [NIRA_BUS_ID_PROPERTY]: niraBusId,
        [BUSES_REMAINING_PROPERTY]: busesRemaining
    });
};

export const segmentTrackSelectionCanceledFromMap = (): void => {
    trackProspectingEvent(SELECTION_CANCELED_FROM_MAP_NAME, {});
};

export const segmentTrackBusSelectionFromMap = (scopedBus: ScopedBus): void => {
    trackProspectingEvent(
        BUS_SELECTED_FROM_MAP_NAME,
        convertScopedBusToSegmentProperties(scopedBus)
    );
};

export const segmentTrackUnauthorizedBusSelectionFromMap = (
    bus: UnauthorizedBus
): void => {
    trackProspectingEvent(UNAUTHORIZED_BUS_SELECTED_FROM_MAP_NAME, {
        [BUS_ID_PROPERTY]: bus.bus.id
    });
};

export const segmentTrackBusSelectionFromToggle = (busId: BusId): void => {
    trackProspectingEvent(BUS_SELECTED_FROM_TOGGLE_NAME, {
        [BUS_ID_PROPERTY]: busId
    });
};

export const segmentTrackMoreDetailsConstraintsDialogOpened = (
    selectedId: BusId | FacilityId
): void => {
    trackProspectingEvent(MORE_DETAILS_CONSTRAINTS_DIALOG_OPENED_NAME, {
        [SELECTED_ID_PROPERTY]: selectedId
    });
};

export const segmentTrackMoreDetailsConstraintsDialogClosed = (
    selectedId: BusId | FacilityId
): void => {
    trackProspectingEvent(MORE_DETAILS_CONSTRAINTS_DIALOG_CLOSED_NAME, {
        [SELECTED_ID_PROPERTY]: selectedId
    });
};

export const segmentTrackSelectionCanceledFromBusDetailPaneCross = (
    busId: BusId
): void => {
    trackProspectingEvent(SELECTION_CANCELED_FROM_BUS_DETAIL_CROSS, {
        [BUS_ID_PROPERTY]: busId
    });
};

export const segmentTrackSelectionCanceledFromLineDetailPaneCross = (
    facilityId: FacilityId
): void => {
    trackProspectingEvent(SELECTION_CANCELED_FROM_LINE_DETAIL_CROSS, {
        [FACILITY_ID_PROPERTY]: facilityId
    });
};

export const segmentTrackConnectedLinesSectionToggled = (
    busId: BusId,
    connectedLinesSectionOpened: boolean
): void => {
    trackProspectingEvent(CONNECTED_LINES_SECTION_TOGGLED_NAME, {
        [BUS_ID_PROPERTY]: busId,
        [CONNECTED_LINES_SECTION_OPENED_PROPERTY]: connectedLinesSectionOpened
    });
};

export const segmentTrackLineSelectedFromBusDetailPane = (
    busId: BusId,
    facilityId: FacilityId
): void => {
    trackProspectingEvent(LINE_SELECTED_FROM_BUS_DETAIL_PANE_NAME, {
        [BUS_ID_PROPERTY]: busId,
        [FACILITY_ID_PROPERTY]: facilityId
    });
};

export const segmentTrackCustomInjectionDialogOpened = (
    selectedId: BusId | FacilityId
): void => {
    trackProspectingEvent(CUSTOM_INJECTION_DIALOG_OPENED_NAME, {
        [SELECTED_ID_PROPERTY]: selectedId
    });
};

export const segmentTrackCustomInjectionDialogClosed = (
    selectedId: BusId | FacilityId
): void => {
    trackProspectingEvent(CUSTOM_INJECTION_DIALOG_CLOSED_NAME, {
        [SELECTED_ID_PROPERTY]: selectedId
    });
};

export const segmentTrackCustomInjectionUpdated = (
    newCapacity: number,
    newEnergy: number,
    newCharging: number,
    previousCapacity: number,
    previousEnergy: number,
    previousCharging: number,
    selectedId: BusId | FacilityId
): void => {
    trackProspectingEvent(CUSTOM_INJECTION_UPDATED_NAME, {
        [EDITED_CAPACITY_AVAILABLE_PROPERTY]: newCapacity,
        [EDITED_ENERGY_AVAILABLE_PROPERTY]: newEnergy,
        [EDITED_CHARGING_AVAILABLE_PROPERTY]: newCharging,
        [PREVIOUS_CAPACITY_AVAILABLE_PROPERTY]: previousCapacity,
        [PREVIOUS_ENERGY_AVAILABLE_PROPERTY]: previousEnergy,
        [PREVIOUS_CHARGING_AVAILABLE_PROPERTY]: previousCharging,
        [SELECTED_ID_PROPERTY]: selectedId
    });
};

export const segmentTrackBusSelectedFromLineDetailPane = (
    busId: BusId,
    facilityId: FacilityId,
    isBusHidden: boolean
): void => {
    trackProspectingEvent(BUS_SELECTED_FROM_LINE_DETAIL_NAME, {
        [BUS_ID_PROPERTY]: busId,
        [FACILITY_ID_PROPERTY]: facilityId,
        [IS_BUS_HIDDEN_PROPERTY]: isBusHidden
    });
};

export const segmentTrackStartedZooming = (
    zoom: number,
    latitude: number,
    longitude: number
): void => {
    trackProspectingEvent(STARTED_ZOOMING_NAME, {
        [ZOOM_PROPERTY]: zoom,
        [LATITUDE_PROPERTY]: latitude,
        [LONGITUDE_PROPERTY]: longitude
    });
};

export const segmentTrackEndedZooming = (
    zoom: number,
    latitude: number,
    longitude: number
): void => {
    trackProspectingEvent(ENDED_ZOOMING_NAME, {
        [ZOOM_PROPERTY]: zoom,
        [LATITUDE_PROPERTY]: latitude,
        [LONGITUDE_PROPERTY]: longitude
    });
};

export const segmentTrackStartedDragging = (
    zoom: number,
    latitude: number,
    longitude: number
): void => {
    trackProspectingEvent(STARTED_DRAGGING_NAME, {
        [ZOOM_PROPERTY]: zoom,
        [LATITUDE_PROPERTY]: latitude,
        [LONGITUDE_PROPERTY]: longitude
    });
};

export const segmentTrackEndedDragging = (
    zoom: number,
    latitude: number,
    longitude: number
): void => {
    trackProspectingEvent(ENDED_DRAGGING_NAME, {
        [ZOOM_PROPERTY]: zoom,
        [LATITUDE_PROPERTY]: latitude,
        [LONGITUDE_PROPERTY]: longitude
    });
};

export const segmentTrackFlyToLocationFromSearch = (
    id: string,
    subText: string
): void => {
    trackProspectingEvent(FLY_TO_LOCATION_FROM_SEARCH_NAME, {
        [SEARCH_RESULT_ID_PROPERTY]: id,
        [SEARCH_RESULT_SUB_TEXT_PROPERTY]: subText
    });
};

export const segmentTrackScopeChanged = (scopeType: ScopeName): void => {
    trackProspectingEvent(SCOPE_CHANGED_NAME, {
        [NEW_SCOPE_TYPE_SELECTED_PROPERTY]: scopeType
    });
};

export const segmentTrackIncludePreExistingChanged = (
    includePreExistingConstraints: boolean
): void => {
    trackProspectingEvent(INCLUDES_PRE_EXISTING_CHANGED_NAME, {
        [INCLUDES_PRE_EXISTING_PROPERTY]: includePreExistingConstraints
    });
};

export const segmentTrackHideLowConfidenceBusesChanged = (
    hideLowConfidenceBuses: boolean
): void => {
    trackProspectingEvent(HIDE_LOW_CONFIDENCE_BUSES_CHANGED_NAME, {
        [HIDE_LOW_CONFIDENCE_BUSES_PROPERTY]: hideLowConfidenceBuses
    });
};

export const segmentTrackHideOutOfRegionBusesChanged = (
    hideOutOfRegionBuses: boolean
): void => {
    trackProspectingEvent(HIDE_OUT_OF_REGION_BUSES_CHANGED_NAME, {
        [HIDE_OUT_OF_REGION_BUSES_PROPERTY]: hideOutOfRegionBuses
    });
};

export const segmentTrackHideLockedBusesChanged = (
    hideLockedBuses: boolean
): void => {
    trackProspectingEvent(HIDE_LOCKED_BUSES_CHANGED_NAME, {
        [HIDE_LOCKED_BUSES_PROPERTY]: hideLockedBuses
    });
};

export const segmentTrackFavoritesFilterChanged = (
    favoritesFilterOn: boolean,
    favoritesCount: number
): void => {
    trackProspectingEvent(FAVORITES_FILTER_CHANGED_NAME, {
        [FAVORITES_FILTER_ON_PROPERTY]: favoritesFilterOn,
        [FAVORITES_COUNT_PROPERTY]: favoritesCount
    });
};

export const segmentTrackFavoritesPanelOpened = (
    source: FavoritesPanelSource,
    favoritesCount: number
): void => {
    trackProspectingEvent(FAVORITES_PANEL_OPENED_NAME, {
        [FAVORITES_PANEL_SOURCE_PROPERTY]: source,
        [FAVORITES_COUNT_PROPERTY]: favoritesCount
    });
};

// Fired on the click, with the state the user chose, like the other toggles.
export const segmentTrackFavoriteBusToggled = (
    niraBusId: NiraBusId,
    favorited: boolean,
    source: FavoriteBusToggleSource
): void => {
    trackProspectingEvent(FAVORITE_BUS_TOGGLED_NAME, {
        [NIRA_BUS_ID_PROPERTY]: niraBusId,
        [FAVORITED_PROPERTY]: favorited,
        [FAVORITE_BUS_TOGGLE_SOURCE_PROPERTY]: source
    });
};

export const segmentTrackColorThresholdsChanged = (
    colorThresholds: CapacityThresholds
): void => {
    trackProspectingEvent(COLOR_THRESHOLDS_CHANGED_NAME, {
        [COLOR_THRESHOLDS_LOW_PROPERTY]: colorThresholds.lowThreshold,
        [COLOR_THRESHOLDS_MEDIUM_PROPERTY]: colorThresholds.mediumThreshold,
        [COLOR_THRESHOLDS_HIGH_PROPERTY]: colorThresholds.highThreshold
    });
};

export const segmentTrackScreeningViewCopyLink = (
    id: ScreeningViewId
): void => {
    trackProspectingEvent(SCREENING_VIEW_COPY_LINK_NAME, {
        [SCREENING_VIEW_ID_PROPERTY]: id
    });
};

export const segmentTrackScreeningViewDelete = (id: ScreeningViewId): void => {
    trackProspectingEvent(SCREENING_VIEW_DELETE_NAME, {
        [SCREENING_VIEW_ID_PROPERTY]: id
    });
};

export const segmentTrackScreeningViewCreate = (id: ScreeningViewId): void => {
    trackProspectingEvent(SCREENING_VIEW_CREATE_NAME, {
        [SCREENING_VIEW_ID_PROPERTY]: id
    });
};

export const segmentTrackScreeningViewExport = (
    id: ScreeningViewId,
    exportType: ExportType
): void => {
    const trackingKey =
        exportType === KMZ_TYPE
            ? SCREENING_VIEW_EXPORT_KMZ_NAME
            : SCREENING_VIEW_EXPORT_SHAPEFILE_NAME;

    trackProspectingEvent(trackingKey, {
        [SCREENING_VIEW_ID_PROPERTY]: id
    });
};

export const segmentTrackSidebarMenuItemClicked = (menuItem: string): void => {
    trackProspectingEvent(SIDEBAR_MENU_ITEM_CLICKED_NAME, {
        [MENU_ITEM_PROPERTY]: menuItem
    });
};

export const segmentTrackDataFreshnessDialogClosed = (
    region: RegionType,
    scope: ScopeName
): void => {
    trackProspectingEvent(DATA_FRESHNESS_DIALOG_CLOSED_NAME, {
        [REGION_PROPERTY]: region,
        [SCOPE_PROPERTY]: scope
    });
};

export const segmentTrackDataFreshnessDownloadQueueProjects = (
    region: RegionType,
    scope: ScopeName,
    scopeView: ScopeViewType
): void => {
    trackProspectingEvent(DATA_FRESHNESS_DIALOG_DOWNLOAD_QUEUE_PROJECTS_NAME, {
        [REGION_PROPERTY]: region,
        [SCOPE_PROPERTY]: scope,
        [SCOPE_VIEW_PROPERTY]: scopeView
    });
};

export const segmentTrackAdvancedStudiesNewAdvancedStudyMenuItemClicked =
    (): void => {
        trackProspectingEvent(
            ADVANCED_STUDIES_NEW_ADVANCED_STUDY_MENU_ITEM_CLICKED_NAME,
            {}
        );
    };

export const segmentTrackAdvancedStudiesViewAdvancedStudiesMenuItemClicked = ({
    source
}: {
    readonly source: ViewAdvancedStudiesSource;
}): void => {
    trackProspectingEvent(
        ADVANCED_STUDIES_VIEW_ADVANCED_STUDIES_MENU_ITEM_CLICKED_NAME,
        {
            [VIEW_ADVANCED_STUDIES_SOURCE_PROPERTY]: source
        }
    );
};

export const segmentTrackAdvancedStudiesNewAdvancedStudyDialogClosed =
    (): void => {
        trackProspectingEvent(
            ADVANCED_STUDIES_NEW_ADVANCED_STUDY_DIALOG_CLOSED_NAME,
            {}
        );
    };

export const segmentTrackAdvancedStudiesNewAdvancedStudyDialogCreateStudy = (
    advancedStudyId: string
): void => {
    trackProspectingEvent(
        ADVANCED_STUDIES_NEW_ADVANCED_STUDY_DIALOG_CREATE_STUDY_CLICKED_NAME,
        {
            [ADVANCED_STUDY_ID_PROPERTY]: advancedStudyId
        }
    );
};

// ---------------------------------------------------------------------------
// Detailed analysis (bus page / local studies) events
// ---------------------------------------------------------------------------

const LAYER_STYLE_PROPERTY = "layer_style";
const SELECTED_FUEL_TYPE_PROPERTY = "selected_fuel_type";
const SELECTED_SCOPE_PROPERTY = "selected_scope";
const SCOPE_TYPE_PROPERTY = "scope_type";
const RESOURCE_TYPE_PROPERTY = "resource_type";
const PROJECT_SIZE_MW_PROPERTY = "project_size_mw";
const DIALOG_STEP_PROPERTY = "dialog_step";
const SUCCESS_PROPERTY = "success";
const NUM_SELECTED_FUEL_TYPES_PROPERTY = "num_selected_fuel_types";
const BASE_SCOPE_PROPERTY = "base_scope";

// Distinguishes Nira-provided pipeline scopes from user-created re-study
// scopes when a scope is selected on the bus page.
export type BusPageScopeType = "nira_scope" | "user_study";

const PERFORM_DETAILED_ANALYSIS_CLICKED_NAME =
    "perform_detailed_analysis_clicked";
export const segmentTrackPerformDetailedAnalysisClicked = (
    busId: BusId,
    niraBusId: NiraBusId
): void => {
    trackProspectingEvent(PERFORM_DETAILED_ANALYSIS_CLICKED_NAME, {
        [BUS_ID_PROPERTY]: busId,
        [NIRA_BUS_ID_PROPERTY]: niraBusId
    });
};

// A load-only team reached the bus page, which only studies generation. The
// page refuses with a non-ideal state; this records the attempt.
const BUS_PAGE_LOAD_ONLY_BLOCKED_NAME = "bus_page_load_only_blocked";
export const segmentTrackBusPageLoadOnlyBlocked = (
    niraBusId: NiraBusId,
    region: RegionType
): void => {
    trackProspectingEvent(BUS_PAGE_LOAD_ONLY_BLOCKED_NAME, {
        [NIRA_BUS_ID_PROPERTY]: niraBusId,
        [REGION_PROPERTY]: region
    });
};

const MAP_LAYER_STYLE_CHANGED_NAME = "map_layer_style_changed";
export const segmentTrackMapLayerStyleChanged = (
    layerStyleId: LayerStyleId
): void => {
    trackProspectingEvent(MAP_LAYER_STYLE_CHANGED_NAME, {
        [LAYER_STYLE_PROPERTY]: layerStyleId
    });
};

// The bus page is its own route (outside the prospecting map view), so these
// events carry their own region property instead of relying on the
// prospecting view context.
const BUS_PAGE_FUEL_TYPE_CHANGED_NAME = "bus_page_fuel_type_changed";
export const segmentTrackBusPageFuelTypeChanged = (
    niraBusId: NiraBusId,
    region: RegionType,
    fuelType: GeneratorType
): void => {
    trackProspectingEvent(BUS_PAGE_FUEL_TYPE_CHANGED_NAME, {
        [NIRA_BUS_ID_PROPERTY]: niraBusId,
        [REGION_PROPERTY]: region,
        [SELECTED_FUEL_TYPE_PROPERTY]: fuelType
    });
};

const BUS_PAGE_SCOPE_CHANGED_NAME = "bus_page_scope_changed";
export const segmentTrackBusPageScopeChanged = (
    niraBusId: NiraBusId,
    region: RegionType,
    scope: ScopeName,
    scopeType: BusPageScopeType
): void => {
    trackProspectingEvent(BUS_PAGE_SCOPE_CHANGED_NAME, {
        [NIRA_BUS_ID_PROPERTY]: niraBusId,
        [REGION_PROPERTY]: region,
        [SELECTED_SCOPE_PROPERTY]: scope,
        [SCOPE_TYPE_PROPERTY]: scopeType
    });
};

const BUS_PAGE_PROJECT_SIZE_CHANGED_NAME = "bus_page_project_size_changed";
export const segmentTrackBusPageProjectSizeChanged = (
    niraBusId: NiraBusId,
    region: RegionType,
    resourceType: ResourceType,
    projectSizeMw: number
): void => {
    trackProspectingEvent(BUS_PAGE_PROJECT_SIZE_CHANGED_NAME, {
        [NIRA_BUS_ID_PROPERTY]: niraBusId,
        [REGION_PROPERTY]: region,
        [RESOURCE_TYPE_PROPERTY]: resourceType,
        [PROJECT_SIZE_MW_PROPERTY]: projectSizeMw
    });
};

// Re-study flow. Events fire at each stage so drop-off through the flow can
// be measured: clicked -> step_viewed (per dialog step) -> submitted ->
// created (with success true/false) -> switch_to_scope_clicked.
//
// "created" is the scope row being written, not the study finishing: the
// backend computes results asynchronously and nothing here observes that
// transition. The name restudy_completed is deliberately left unused so it
// stays available if a real study-completion event ever gets emitted.
const RESTUDY_SOURCE_PROPERTY = "source";
export type RestudySource = "user" | "auto_stress";

const RESTUDY_BUS_CLICKED_NAME = "restudy_bus_clicked";
export const segmentTrackRestudyBusClicked = (
    niraBusId: NiraBusId,
    region: RegionType
): void => {
    trackProspectingEvent(RESTUDY_BUS_CLICKED_NAME, {
        [NIRA_BUS_ID_PROPERTY]: niraBusId,
        [REGION_PROPERTY]: region
    });
};

const RESTUDY_STEP_VIEWED_NAME = "restudy_step_viewed";
export const segmentTrackRestudyStepViewed = (
    niraBusId: NiraBusId,
    region: RegionType,
    dialogStep: string
): void => {
    trackProspectingEvent(RESTUDY_STEP_VIEWED_NAME, {
        [NIRA_BUS_ID_PROPERTY]: niraBusId,
        [REGION_PROPERTY]: region,
        [DIALOG_STEP_PROPERTY]: dialogStep
    });
};

const RESTUDY_SUBMITTED_NAME = "restudy_submitted";
export const segmentTrackRestudySubmitted = (
    niraBusId: NiraBusId,
    region: RegionType,
    baseScope: ScopeName,
    numSelectedFuelTypes: number
): void => {
    trackProspectingEvent(RESTUDY_SUBMITTED_NAME, {
        [NIRA_BUS_ID_PROPERTY]: niraBusId,
        [REGION_PROPERTY]: region,
        [BASE_SCOPE_PROPERTY]: baseScope,
        [NUM_SELECTED_FUEL_TYPES_PROPERTY]: numSelectedFuelTypes
    });
};

const RESTUDY_CREATED_NAME = "restudy_created";
export const segmentTrackRestudyCreated = (
    niraBusId: NiraBusId,
    region: RegionType,
    source: RestudySource,
    success: boolean
): void => {
    trackProspectingEvent(RESTUDY_CREATED_NAME, {
        [NIRA_BUS_ID_PROPERTY]: niraBusId,
        [REGION_PROPERTY]: region,
        [RESTUDY_SOURCE_PROPERTY]: source,
        [SUCCESS_PROPERTY]: success
    });
};

const RESTUDY_SWITCH_TO_SCOPE_CLICKED_NAME = "restudy_switch_to_scope_clicked";
export const segmentTrackRestudySwitchToScopeClicked = (
    niraBusId: NiraBusId,
    region: RegionType,
    scope: ScopeName,
    source: RestudySource
): void => {
    trackProspectingEvent(RESTUDY_SWITCH_TO_SCOPE_CLICKED_NAME, {
        [NIRA_BUS_ID_PROPERTY]: niraBusId,
        [REGION_PROPERTY]: region,
        [SELECTED_SCOPE_PROPERTY]: scope,
        [RESTUDY_SOURCE_PROPERTY]: source
    });
};

const RESTUDY_KEEP_BASE_ASSUMPTIONS_CLICKED_NAME =
    "restudy_keep_base_assumptions_clicked";
export const segmentTrackRestudyKeepBaseAssumptionsClicked = (
    niraBusId: NiraBusId,
    region: RegionType
): void => {
    trackProspectingEvent(RESTUDY_KEEP_BASE_ASSUMPTIONS_CLICKED_NAME, {
        [NIRA_BUS_ID_PROPERTY]: niraBusId,
        [REGION_PROPERTY]: region
    });
};

// The two events below track the prompt a user gets when the study they are
// about to create matches the base scope: base toggles and no new generators.
// Declining is "shown without confirmed". restudy_submitted fires only after
// the user confirms, so it does not count these on its own.
const RESTUDY_UNCHANGED_CONFIRMATION_SHOWN_NAME =
    "restudy_unchanged_confirmation_shown";
export const segmentTrackRestudyUnchangedConfirmationShown = (
    niraBusId: NiraBusId,
    region: RegionType,
    baseScope: ScopeName
): void => {
    trackProspectingEvent(RESTUDY_UNCHANGED_CONFIRMATION_SHOWN_NAME, {
        [NIRA_BUS_ID_PROPERTY]: niraBusId,
        [REGION_PROPERTY]: region,
        [BASE_SCOPE_PROPERTY]: baseScope
    });
};

const RESTUDY_UNCHANGED_CONFIRMATION_CONFIRMED_NAME =
    "restudy_unchanged_confirmation_confirmed";
export const segmentTrackRestudyUnchangedConfirmationConfirmed = (
    niraBusId: NiraBusId,
    region: RegionType,
    baseScope: ScopeName
): void => {
    trackProspectingEvent(RESTUDY_UNCHANGED_CONFIRMATION_CONFIRMED_NAME, {
        [NIRA_BUS_ID_PROPERTY]: niraBusId,
        [REGION_PROPERTY]: region,
        [BASE_SCOPE_PROPERTY]: baseScope
    });
};

// Fired on app load and on team change, immediately after the account group()
// association is established (see SegmentGroupProvider). SegmentProvider's
// first page()/identify() of a cold load can be emitted before the group is
// set, so that landing event may be ungrouped. This event fires *after*
// group() and is therefore guaranteed to carry the account, giving analytics a
// company-stamped event to anchor on. It is not once per Amplitude session: a
// reload inside a live session emits a second one, and a session that rolls
// over while the app stays mounted emits none, so count sessions off
// session_id rather than this event. Uses the base track() (not
// trackProspectingEvent) because it is an app-level event with no prospecting
// view context.
const ACCOUNT_LOADED_NAME = "account_loaded";
const TEAM_ID_PROPERTY = "team_id";
const TEAM_NAME_PROPERTY = "team_name";
export const segmentTrackAccountLoaded = (
    teamId: TeamId,
    teamName: string
): void => {
    track(ACCOUNT_LOADED_NAME, {
        [TEAM_ID_PROPERTY]: teamId,
        [TEAM_NAME_PROPERTY]: teamName
    });
};

// Fired when the user clicks Logout, before the analytics identity is cleared,
// so it still carries the user and account it belongs to. This is the click on
// the menu item, not a general "session ended": an auth0 session that simply
// expires emits nothing. The callback runs once analytics.js has handed the
// event off (immediately when metrics are disabled), so the caller can let it
// leave before auth0 navigates the page away. Uses the base track() because it
// is an app-level event with no prospecting view context.
const LOGOUT_CLICKED_NAME = "logout_clicked";
export const segmentTrackLogoutClicked = (callback: () => void): void => {
    track(LOGOUT_CLICKED_NAME, {}, callback);
};

const convertScopedBusToSegmentProperties = (
    scopedBus: ScopedBus
): Record<string, string | number> => {
    return {
        ...convertBusToSegmentTrackedObject(scopedBus.bus),
        ...convertCapacityEnergyCostsToSegmentProperties(
            scopedBus.scopedCapacityEnergyCost
        )
    };
};

const convertCapacityEnergyCostsToSegmentProperties = (
    capacityEnergyCosts: CapacityEnergyCost
): Record<string, number> => {
    return {
        [CAPACITY_AVAILABLE_PROPERTY]: capacityEnergyCosts.capacitySize,
        [ENERGY_AVAILABLE_PROPERTY]: capacityEnergyCosts.energySize,
        [ALLOCATED_COSTS_PROPERTY]: capacityEnergyCosts.allocatedCosts,
        [TOTAL_COSTS_PROPERTY]: capacityEnergyCosts.totalCosts
    };
};

const convertBusToSegmentTrackedObject = (
    bus: Bus
): Record<string, string | number> => {
    return {
        [BUS_ID_PROPERTY]: bus.id,
        [BUS_DISPLAY_NAME_PROPERTY]: bus.busDisplayName,
        [BUS_VOLTAGE_PROPERTY]: bus.voltage,
        [BUS_REGION_PROPERTY]: bus.region,
        [BUS_OWNER_NAME_PROPERTY]: bus.ownerName
    };
};
